import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Database, FileDown, Trash2, Loader2 } from "lucide-react";
import { downloadExport } from "@/lib/downloadExport";
import { toast } from "sonner";

/** Starlink demo-data purge (super admin) + downloadable Excel backups. */
export default function DataBackupsPanel() {
  const { user } = useAuth();
  const isSuper = !!user?.is_super_admin || user?.role === "super_admin";
  const [backups, setBackups] = useState([]);
  const [counts, setCounts] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/data-backups").then((r) => setBackups(r.data.backups || [])).catch(() => {});
    if (isSuper) api.post("/admin/starlink/purge-demo?dry_run=true").then((r) => setCounts(r.data)).catch(() => {});
  }, [isSuper]);
  useEffect(() => { load(); }, [load]);

  const purge = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/starlink/purge-demo");
      toast.success(`Deleted ${data.tickets} tickets and ${data.customers} customers — backup saved. Ticket numbering restarts at TKT-001.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purge failed");
    } finally { setBusy(false); }
  };

  const download = (b) => downloadExport(`/data-backups/${b.id}/download`, {}, b.file_name)
    .then(() => toast.success("Backup downloaded")).catch(() => toast.error("Download failed"));

  return (
    <Card data-testid="data-backups-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2"><Database className="w-4 h-4 text-slate-500" /> Data &amp; backups</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-xs">
        {isSuper && counts && (
          <div className="flex flex-wrap items-center gap-2 border border-rose-200 bg-rose-50 rounded p-2" data-testid="purge-card">
            <span className="text-rose-900 flex-1 min-w-[240px]">
              <strong>Clean out demo data</strong> — permanently deletes <strong data-testid="purge-tickets">{counts.tickets}</strong> Starlink
              tickets (with comments, CSAT, email log, notifications) and <strong data-testid="purge-customers">{counts.customers}</strong> customers
              (with tags/segments). An Excel backup is produced first. Users, roles, KB, taxonomy and channel config are untouched.
            </span>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" className="h-7 text-[10px] bg-rose-600 hover:bg-rose-700" disabled={busy || (!counts.tickets && !counts.customers)}
                        data-testid="purge-btn">
                  {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Trash2 className="w-3 h-3 mr-1" />} Back up &amp; delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete all Starlink tickets and customers?</AlertDialogTitle>
                  <AlertDialogDescription>
                    {counts.tickets} tickets and {counts.customers} customers will be permanently removed after an Excel backup is stored here.
                    Ticket numbering restarts at TKT-001. This cannot be undone from the UI.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={purge} className="bg-rose-600 hover:bg-rose-700" data-testid="purge-confirm">Back up &amp; delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
        {backups.length === 0 ? (
          <div className="text-slate-400 py-3 text-center">No backups yet.</div>
        ) : backups.map((b) => (
          <div key={b.id} className="flex items-center justify-between gap-2 border border-slate-200 rounded px-2 py-1.5" data-testid={`backup-${b.id}`}>
            <div className="min-w-0">
              <div className="font-medium text-slate-800 truncate">{b.file_name}</div>
              <div className="text-[10px] text-slate-500">{new Date(b.created_at).toLocaleString()} · {b.created_by} ·
                <Badge variant="outline" className="ml-1 text-[9px]">{b.summary?.tickets ?? 0} tickets</Badge>
                <Badge variant="outline" className="ml-1 text-[9px]">{b.summary?.customers ?? 0} customers</Badge>
              </div>
            </div>
            <Button variant="outline" size="sm" className="h-7 text-[10px]" onClick={() => download(b)} data-testid={`backup-download-${b.id}`}>
              <FileDown className="w-3 h-3 mr-1" /> Download
            </Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
